import { Box, Card, Typography, useTheme } from '@mui/material';
import type { CSSProperties, FC, ReactNode } from 'react';

export const securityDashboardCardSx = {
  backgroundColor: '#FFFFFF',
  border: '1px solid #E2E8F0',
  borderRadius: '10px',
  boxShadow: 'none',
} as const;

export const securityDashboardCardHeaderSx = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  paddingTop: '12px',
  paddingRight: '16px',
  paddingBottom: '12px',
  paddingLeft: '16px',
  borderBottom: '1px solid #E2E8F0',
  backgroundColor: '#F8FAFC80',
} as const;

export const securityDashboardCardTitleStyle: React.CSSProperties = {
  fontFamily: 'Inter, sans-serif',
  fontWeight: 700,
  fontSize: 14,
  lineHeight: '21px',
  letterSpacing: 0,
  color: '#1E293B',
  verticalAlign: 'middle',
};

export const securityDashboardCardFooterSx = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '16px',
  paddingTop: '12px',
  paddingRight: '16px',
  paddingBottom: '12px',
  paddingLeft: '16px',
  borderTop: '1px solid #F1F5F9',
} as const;

export const securityDashboardCardBodySx = {
  padding: '16px',
} as const;

export const securityDashboardSuccessColor = '#22C55E';

const SECURITY_TIMELINE_DOT_SIZE = 8;
const SECURITY_TIMELINE_RAIL_WIDTH = 10;
const SECURITY_TIMELINE_ITEM_PADDING_TOP = 14;
const SECURITY_TIMELINE_DOT_OFFSET_TOP = 4;
const SECURITY_TIMELINE_ITEM_PADDING_X = 16;
const SECURITY_TIMELINE_LINE_BOTTOM_GAP = 14;

/** Vertical connector on every item; horizontal divider inset to match content padding. */
export const SecurityTimelineListItemShell: FC<{
  isLast: boolean;
  children: ReactNode;
  /** Dot fill color. Defaults to brand-critical red used on dashboards. */
  dotColor?: string;
}> = ({ isLast, children, dotColor = '#E53E3E' }) => (
  <Box
    position="relative"
    display="flex"
    style={{
      gap: 12,
      padding: '14px 16px',
    }}
  >
    {!isLast ? (
      <Box
        position="absolute"
        style={{
          left: SECURITY_TIMELINE_ITEM_PADDING_X,
          right: SECURITY_TIMELINE_ITEM_PADDING_X,
          bottom: 0,
          height: 1,
          backgroundColor: '#F1F5F9',
        }}
      />
    ) : null}
    <Box
      position="absolute"
      style={{
        left:
          SECURITY_TIMELINE_ITEM_PADDING_X
          + SECURITY_TIMELINE_RAIL_WIDTH / 2
          - 0.5,
        top:
          SECURITY_TIMELINE_ITEM_PADDING_TOP
          + SECURITY_TIMELINE_DOT_OFFSET_TOP
          + SECURITY_TIMELINE_DOT_SIZE,
        bottom: SECURITY_TIMELINE_LINE_BOTTOM_GAP,
        width: 1,
        backgroundColor: '#E2E8F0',
      }}
    />
    <Box
      display="flex"
      justifyContent="center"
      style={{
        width: SECURITY_TIMELINE_RAIL_WIDTH,
        flexShrink: 0,
        paddingTop: SECURITY_TIMELINE_DOT_OFFSET_TOP,
      }}
    >
      <Box
        style={{
          position: 'relative',
          zIndex: 1,
          width: SECURITY_TIMELINE_DOT_SIZE,
          height: SECURITY_TIMELINE_DOT_SIZE,
          borderRadius: '50%',
          backgroundColor: dotColor,
          flexShrink: 0,
        }}
      />
    </Box>
    <Box style={{ flex: 1, minWidth: 0 }}>{children}</Box>
  </Box>
);

/** Minimum height for chart cards in the dashboard grid row (fits donut + 5-row legend when stacked). */
export const SECURITY_SEVERITY_CHART_CARD_MIN_HEIGHT = 368;

type SecurityDashboardCardHeaderProps = {
  icon?: ReactNode;
  title: string;
  right?: ReactNode;
};

export const SecurityDashboardCardHeader: FC<SecurityDashboardCardHeaderProps> = ({
  icon,
  title,
  right,
}) => {
  const theme = useTheme();

  return (
    <Box sx={securityDashboardCardHeaderSx}>
      {icon ? (
        <Box
          display="flex"
          alignItems="center"
          style={{
            color: theme.palette.primary.main,
            lineHeight: 0,
          }}
        >
          {icon}
        </Box>
      ) : null}
      <Typography component="span" style={securityDashboardCardTitleStyle}>
        {title}
      </Typography>
      {right ? <Box style={{ marginLeft: 'auto' }}>{right}</Box> : null}
    </Box>
  );
};

type SecurityDashboardCardFooterProps = {
  children: ReactNode;
  align?: 'center' | 'space-between';
};

export const SecurityDashboardCardFooter: FC<SecurityDashboardCardFooterProps> = ({
  children,
  align = 'center',
}) => (
  <Box
    sx={{
      ...securityDashboardCardFooterSx,
      justifyContent: align === 'space-between' ? 'space-between' : 'center',
    }}
  >
    {children}
  </Box>
);

type SecurityDashboardCardBodyProps = {
  children: ReactNode;
  style?: CSSProperties;
};

export const SecurityDashboardCardBody: FC<SecurityDashboardCardBodyProps> = ({
  children,
  style,
}) => (
  <Box sx={securityDashboardCardBodySx} style={style}>
    {children}
  </Box>
);

type SecurityDashboardCardProps = {
  children: ReactNode;
  title?: string;
  icon?: ReactNode;
  headerRight?: ReactNode;
  footer?: ReactNode;
  footerAlign?: 'center' | 'space-between';
  /** Wrap children with standard 16px body padding. */
  bodyPadding?: boolean;
  /** When true, card body sizes to content instead of stretching to fill grid row height. */
  fitContent?: boolean;
  overflow?: 'hidden' | 'visible';
  style?: CSSProperties;
};

export const SecurityDashboardCard: FC<SecurityDashboardCardProps> = ({
  children,
  title,
  icon,
  headerRight,
  footer,
  footerAlign = 'center',
  bodyPadding = false,
  fitContent = false,
  overflow = 'visible',
  style,
}) => (
  <Card
    sx={securityDashboardCardSx}
    style={{
      display: 'flex',
      flexDirection: 'column',
      overflow,
      ...style,
    }}
  >
    {title ? (
      <SecurityDashboardCardHeader icon={icon} title={title} right={headerRight} />
    ) : null}
    <Box
      style={{
        display: 'flex',
        flexDirection: 'column',
        ...(fitContent ? {} : { flex: 1, minHeight: 0 }),
        minWidth: 0,
        width: '100%',
      }}
    >
      {bodyPadding ? (
        <Box
          sx={{
            ...securityDashboardCardBodySx,
            ...(fitContent ? {} : { flex: 1 }),
            minWidth: 0,
            width: '100%',
          }}
        >
          {children}
        </Box>
      ) : (
        <Box
          style={{
            display: 'flex',
            flexDirection: 'column',
            ...(fitContent ? {} : { flex: 1, minHeight: 0 }),
            minWidth: 0,
            width: '100%',
          }}
        >
          {children}
        </Box>
      )}
    </Box>
    {footer ? (
      <SecurityDashboardCardFooter align={footerAlign}>{footer}</SecurityDashboardCardFooter>
    ) : null}
  </Card>
);

const SEVERITY_LABELS = ['CRITICAL', 'HIGH', 'MEDIUM', 'LOW'] as const;

const SEVERITY_FOREGROUND_COLORS = [
  '#EF4444',
  '#F9A8D4',
  '#FECACA',
  '#FDE68A',
  '#63B3ED',
  '#38B2AC',
  '#ED8936',
  '#805AD5',
] as const;

const BAR_AREA_HEIGHT = 168;
const BAR_MIN_WIDTH = 20;
const BAR_MAX_WIDTH = 56;

const formatBarCount = (value: number): string =>
  Number.isFinite(value) ? value.toLocaleString() : '0';

const ChartLegendItem: FC<{ color: string; label: string }> = ({ color, label }) => (
  <Box display="flex" alignItems="center" style={{ gap: 6 }}>
    <Box
      style={{
        width: 8,
        height: 8,
        borderRadius: '50%',
        backgroundColor: color,
        flexShrink: 0,
      }}
    />
    <Typography
      style={{
        fontFamily: 'Inter, sans-serif',
        fontSize: 12,
        lineHeight: '18px',
        color: '#64748B',
      }}
    >
      {label}
    </Typography>
  </Box>
);

type SecuritySeverityCompareBarChartProps = {
  last30Days: number[];
  last7Days: number[];
  /** Defaults to CRITICAL/HIGH/MEDIUM/LOW; length drives bar count when data arrays differ. */
  labels?: string[];
  /** When true, bars expand to fill each column (endpoint Security Overview). */
  fill?: boolean;
};

export const SecuritySeverityCompareBarChart: FC<SecuritySeverityCompareBarChartProps> = ({
  last30Days,
  last7Days,
  labels: labelsProp,
  fill = false,
}) => {
  const barWidthPercent = fill ? '92%' : '55%';
  const barMaxWidth = fill ? undefined : BAR_MAX_WIDTH;
  const columnGap = fill ? 4 : 8;
  const barAreaHeight = fill ? 140 : BAR_AREA_HEIGHT;
  const barCount = Math.max(last30Days.length, last7Days.length, 1);
  const defaultLabels: string[] = [...SEVERITY_LABELS];
  const labels: string[] =
    labelsProp && labelsProp.length >= barCount
      ? labelsProp.slice(0, barCount)
      : [
        ...defaultLabels.slice(0, barCount),
        ...Array.from({ length: Math.max(0, barCount - defaultLabels.length) }, (_, i) =>
          `LEVEL ${defaultLabels.length + i + 1}`
        ),
      ];

  const maxValue = Math.max(
    ...last30Days.slice(0, barCount),
    ...last7Days.slice(0, barCount),
    1
  );

  return (
    <Box
      display="flex"
      flexDirection="column"
      style={{ flex: 1, height: '100%', minHeight: 0, width: '100%' }}
    >
      <Box
        sx={{
          ...securityDashboardCardBodySx,
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: fill ? 'center' : 'flex-end',
          minHeight: 0,
        }}
      >
        <Box
          display="flex"
          alignItems="flex-end"
          justifyContent="stretch"
          style={{
            gap: columnGap,
            minHeight: barAreaHeight + 28,
            ...(fill ? { flex: 1 } : {}),
            width: '100%',
          }}
        >
          {labels.map((label, index) => {
            const value30 = last30Days[index] ?? 0;
            const value7 = last7Days[index] ?? 0;
            const backgroundHeight = (value30 / maxValue) * barAreaHeight;
            const foregroundHeight = (value7 / maxValue) * barAreaHeight;
            const foregroundColor =
              SEVERITY_FOREGROUND_COLORS[index % SEVERITY_FOREGROUND_COLORS.length];

            return (
              <Box
                key={label}
                display="flex"
                flexDirection="column"
                alignItems="center"
                sx={{
                  flex: '1 1 0',
                  minWidth: 0,
                  cursor: 'default',
                  '&:hover .severity-bar-bg': {
                    backgroundColor: '#CBD5E1',
                  },
                  '&:hover .severity-bar-fg': {
                    filter: 'brightness(1.08)',
                    boxShadow: `0 0 0 1px ${foregroundColor}33`,
                  },
                  '&:hover .severity-bar-tooltip': {
                    opacity: 1,
                    transform: 'translateX(-50%) translateY(0)',
                  },
                  '&:hover .severity-bar-label': {
                    color: '#64748B',
                  },
                }}
              >
                <Box
                  position="relative"
                  display="flex"
                  alignItems="flex-end"
                  justifyContent="center"
                  style={{
                    width: '100%',
                    height: barAreaHeight,
                  }}
                >
                  <Box
                    className="severity-bar-tooltip"
                    sx={{
                      position: 'absolute',
                      left: '50%',
                      top: 4,
                      zIndex: 2,
                      opacity: 0,
                      transform: 'translateX(-50%) translateY(4px)',
                      transition: 'opacity 160ms ease, transform 160ms ease',
                      pointerEvents: 'none',
                      minWidth: 118,
                      maxWidth: 160,
                      padding: '8px 10px',
                      borderRadius: '8px',
                      backgroundColor: '#0F172A',
                      boxShadow: '0 8px 20px rgba(15, 23, 42, 0.18)',
                    }}
                  >
                    <Typography
                      style={{
                        fontFamily: 'Inter, sans-serif',
                        fontSize: 11,
                        fontWeight: 600,
                        lineHeight: '14px',
                        color: '#F8FAFC',
                        marginBottom: 6,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {label}
                    </Typography>
                    <Box display="flex" flexDirection="column" style={{ gap: 4 }}>
                      <Box display="flex" alignItems="center" justifyContent="space-between" style={{ gap: 10 }}>
                        <Box display="flex" alignItems="center" style={{ gap: 6, minWidth: 0 }}>
                          <Box
                            style={{
                              width: 6,
                              height: 6,
                              borderRadius: '50%',
                              backgroundColor: '#CBD5E0',
                              flexShrink: 0,
                            }}
                          />
                          <Typography
                            style={{
                              fontFamily: 'Inter, sans-serif',
                              fontSize: 10,
                              lineHeight: '14px',
                              color: '#94A3B8',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            30 days
                          </Typography>
                        </Box>
                        <Typography
                          style={{
                            fontFamily: 'Inter, sans-serif',
                            fontSize: 11,
                            fontWeight: 700,
                            lineHeight: '14px',
                            color: '#F8FAFC',
                          }}
                        >
                          {formatBarCount(value30)}
                        </Typography>
                      </Box>
                      <Box display="flex" alignItems="center" justifyContent="space-between" style={{ gap: 10 }}>
                        <Box display="flex" alignItems="center" style={{ gap: 6, minWidth: 0 }}>
                          <Box
                            style={{
                              width: 6,
                              height: 6,
                              borderRadius: '50%',
                              backgroundColor: foregroundColor,
                              flexShrink: 0,
                            }}
                          />
                          <Typography
                            style={{
                              fontFamily: 'Inter, sans-serif',
                              fontSize: 10,
                              lineHeight: '14px',
                              color: '#94A3B8',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            7 days
                          </Typography>
                        </Box>
                        <Typography
                          style={{
                            fontFamily: 'Inter, sans-serif',
                            fontSize: 11,
                            fontWeight: 700,
                            lineHeight: '14px',
                            color: '#F8FAFC',
                          }}
                        >
                          {formatBarCount(value7)}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                  <Box
                    className="severity-bar-bg"
                    position="absolute"
                    bottom={0}
                    style={{
                      width: barWidthPercent,
                      minWidth: BAR_MIN_WIDTH,
                      maxWidth: barMaxWidth,
                      height: Math.max(backgroundHeight, 4),
                      backgroundColor: '#E2E8F0',
                      borderRadius: '6px 6px 0 0',
                      transition: 'background-color 160ms ease',
                    }}
                  />
                  <Box
                    className="severity-bar-fg"
                    position="absolute"
                    bottom={0}
                    style={{
                      width: barWidthPercent,
                      minWidth: BAR_MIN_WIDTH,
                      maxWidth: barMaxWidth,
                      height: Math.max(foregroundHeight, 4),
                      backgroundColor: foregroundColor,
                      borderRadius: '6px 6px 0 0',
                      transition: 'filter 160ms ease, box-shadow 160ms ease',
                    }}
                  />
                </Box>
                <Typography
                  className="severity-bar-label"
                  style={{
                    marginTop: 10,
                    fontFamily: 'Inter, sans-serif',
                    fontSize: 10,
                    fontWeight: 600,
                    lineHeight: '15px',
                    letterSpacing: '0.04em',
                    color: '#94A3B8',
                    textAlign: 'center',
                    width: '100%',
                    whiteSpace: 'normal',
                    wordBreak: 'break-word',
                    transition: 'color 160ms ease',
                  }}
                >
                  {fill ? label.toUpperCase() : label}
                </Typography>
              </Box>
            );
          })}
        </Box>
      </Box>
      <Box style={{ flexShrink: 0, marginTop: 'auto' }}>
        <SecurityDashboardCardFooter>
          <ChartLegendItem color="#CBD5E0" label="Last 30days" />
          <ChartLegendItem color="#63B3ED" label="Last 7 days" />
        </SecurityDashboardCardFooter>
      </Box>
    </Box>
  );
};

export const securitySecurityScoreCardSx = {
  backgroundColor: '#12B6F80D',
  border: '1px solid #12B6F833',
  borderRadius: '10px',
  boxShadow: 'none',
} as const;

export const securitySecurityScoreHeaderTitleStyle: React.CSSProperties = {
  fontFamily: 'Inter, sans-serif',
  fontWeight: 700,
  fontSize: 14,
  lineHeight: '20px',
  letterSpacing: 0,
  verticalAlign: 'middle',
};

export const securitySecurityScoreCardPadding = '12px';

export const securitySecurityScoreImpactBoxSx = {
  width: '100%',
  minHeight: 56,
  borderRadius: '7px',
  padding: '6px 8px',
  backgroundColor: '#FFFFFF80',
  boxSizing: 'border-box',
} as const;

export type SecuritySecurityScoreImpactRow = {
  id: string;
  label: string;
  impact: string;
};

type SecuritySecurityScoreCardProps = {
  icon: ReactNode;
  score: string;
  comparison: string;
  impactRows: readonly SecuritySecurityScoreImpactRow[];
  style?: CSSProperties;
};

export const SecuritySecurityScoreCard: FC<SecuritySecurityScoreCardProps> = ({
  icon,
  score,
  comparison,
  impactRows,
  style,
}) => {
  const theme = useTheme();
  const primaryColor = theme.palette.primary.main;

  return (
    <Card
      sx={securitySecurityScoreCardSx}
      style={{
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        ...style,
      }}
    >
      <Box sx={{ padding: securitySecurityScoreCardPadding }}>
        <Box display="flex" alignItems="center" style={{ gap: 6, marginBottom: 8 }}>
          <Box display="flex" alignItems="center" style={{ color: primaryColor, lineHeight: 0 }}>
            {icon}
          </Box>
          <Typography
            component="span"
            style={{ ...securitySecurityScoreHeaderTitleStyle, color: primaryColor }}
          >
            Security Score
          </Typography>
        </Box>

        <Box display="flex" alignItems="baseline" style={{ gap: 6, marginBottom: 8 }}>
          <Typography
            style={{
              fontFamily: 'Inter, sans-serif',
              fontSize: 24,
              fontWeight: 700,
              lineHeight: 1.1,
              color: '#1A202C',
            }}
          >
            {score}
          </Typography>
          <Typography
            style={{
              fontFamily: 'Inter, sans-serif',
              fontSize: 12,
              fontWeight: 600,
              lineHeight: '16px',
              color: securityDashboardSuccessColor,
            }}
          >
            {comparison}
          </Typography>
        </Box>

        <Box sx={securitySecurityScoreImpactBoxSx}>
          <Box display="flex" flexDirection="column" style={{ gap: 4 }}>
            {impactRows.map((row) => (
              <Box
                key={row.id}
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                style={{ gap: 8 }}
              >
                <Typography
                  style={{
                    fontFamily: 'Inter, sans-serif',
                    fontSize: 11,
                    lineHeight: '16px',
                    color: '#4A5568',
                  }}
                >
                  {row.label}
                </Typography>
                <Typography
                  style={{
                    fontFamily: 'Inter, sans-serif',
                    fontSize: 11,
                    fontWeight: 600,
                    lineHeight: '16px',
                    color: '#718096',
                    flexShrink: 0,
                  }}
                >
                  {row.impact}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    </Card>
  );
};
